import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { FeedbackKind, useSubmitFeedback } from "../api/feedback";

interface Props {
  open: boolean;
  onClose: () => void;
}

const TAB_PLACEHOLDERS: Record<FeedbackKind, { title: string; body: string }> = {
  bug: {
    title: "e.g. Re-analyze button stays enabled when clicked twice",
    body: "What were you doing? What happened? What did you expect to happen?",
  },
  feature: {
    title: "e.g. Show pre-market price on Watchlist",
    body: "What problem would this solve? Who else might want it?",
  },
  other: { title: "", body: "" },
};

export default function FeedbackDialog({ open, onClose }: Props) {
  const submit = useSubmitFeedback();
  const location = useLocation();
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submittedAt, setSubmittedAt] = useState<number | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setKind("bug");
      setTitle("");
      setBody("");
      setSubmittedAt(null);
      submit.reset();
      return;
    }
    // Focus the title input on open so the user can type immediately.
    setTimeout(() => titleRef.current?.focus(), 50);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-close two seconds after a successful submit so the toast settles.
  useEffect(() => {
    if (!submittedAt) return;
    const id = setTimeout(onClose, 1800);
    return () => clearTimeout(id);
  }, [submittedAt, onClose]);

  if (!open) return null;

  const placeholders = TAB_PLACEHOLDERS[kind];
  const browser = navigator.userAgent;
  const pageUrl = location.pathname + location.search;

  const errorDetail = submit.error
    ? extractError(submit.error)
    : null;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    const b = body.trim();
    if (!t || !b) return;
    submit.mutate(
      { kind, title: t, body: b, page_url: pageUrl },
      { onSuccess: () => setSubmittedAt(Date.now()) },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-slate-100">
              Send feedback
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Track status in <span className="font-medium text-slate-300">Settings → Your feedback</span>.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
          >
            ×
          </button>
        </div>

        {submittedAt ? (
          <div className="rounded-lg border border-emerald-900 bg-emerald-950/40 p-4 text-sm text-emerald-300">
            Thanks — submitted. Track status in Settings.
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="mb-3 flex gap-1 rounded-full bg-slate-950 p-0.5 ring-1 ring-slate-800">
              {(["bug", "feature"] as FeedbackKind[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`flex-1 rounded-full px-3 py-1.5 text-xs capitalize transition ${
                    kind === k
                      ? "bg-slate-800 text-slate-100"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {k === "bug" ? "Report a bug" : "Suggest a feature"}
                </button>
              ))}
            </div>

            <label className="mb-3 block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-400">
                Title
              </span>
              <input
                ref={titleRef}
                required
                maxLength={120}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={placeholders.title}
                className="w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-sky-500/40 placeholder:text-slate-600 focus:border-sky-500 focus:ring-2"
              />
            </label>

            <label className="mb-3 block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-400">
                Details
              </span>
              <textarea
                required
                maxLength={2000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={placeholders.body}
                rows={5}
                className="w-full resize-y rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-sky-500/40 placeholder:text-slate-600 focus:border-sky-500 focus:ring-2"
              />
              <span className="mt-1 block text-right text-[10px] text-slate-500">
                {body.length} / 2000
              </span>
            </label>

            <div className="mb-4 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-[11px] text-slate-500">
              <div>
                <span className="text-slate-400">Page:</span>{" "}
                <span className="font-mono">{pageUrl}</span>
              </div>
              <div className="mt-0.5 truncate">
                <span className="text-slate-400">Browser:</span>{" "}
                <span className="font-mono">{browser}</span>
              </div>
            </div>

            {errorDetail && (
              <div className="mb-3 rounded-lg border border-rose-900 bg-rose-950/40 p-3 text-xs text-rose-300">
                {errorDetail}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submit.isPending || !title.trim() || !body.trim()}
                className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-sky-400 active:bg-sky-600 disabled:opacity-50"
              >
                {submit.isPending ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function extractError(err: Error): string {
  // axios error shape — surface the server's `detail.message` or fallback.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resp = (err as any)?.response;
  if (resp?.status === 429) {
    const msg = resp.data?.detail?.message ?? "Daily limit reached.";
    return msg;
  }
  return err.message || "Couldn't submit. Please try again.";
}
